import React, { useState, useEffect, useRef } from 'react';
import { ExpenseLineItem, ReceiptMetadata, MileageRoute, listMileageRoutes, saveMileageRoute, uploadRouteReceipt, deleteMileageRoute, getReceiptPreviewUrl, getRouteReceiptPreviewUrl } from '../../services/api';
import { useApiToken } from '../../auth/AuthProvider';
import { getCategoryStyle } from '../../services/categoryUtils';
import ReceiptPreviewModal from '../../components/ReceiptPreviewModal';
import ConfirmModal from '../../components/ConfirmModal';
import InputModal from '../../components/InputModal';

const CATEGORIES = [
  'Meals', 'Airfare', 'Lodging', 'Transp.', 'Mileage', 'Rental Car',
  'Computer + Office Supplies', 'Learning + Development', 'Conference Registration',
  'Memberships + Subscriptions', 'Service Fees', 'Alcohol', 'Other',
];

const MILEAGE_RATE = 0.70;

interface Props {
  expenses: ExpenseLineItem[];
  receipts: ReceiptMetadata[];
  reason: string;
  startDate?: string;
  endDate?: string;
  onChange: (expenses: ExpenseLineItem[]) => void;
  onNext: () => void;
  onBack: () => void;
  nextLabel?: string;
  backLabel?: string;
  saving?: boolean;
}

const styles: Record<string, React.CSSProperties> = {
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '8px', background: '#f7fafc', borderBottom: '2px solid #e2e8f0', fontSize: 12, fontWeight: 600 },
  td: { padding: '6px 8px', borderBottom: '1px solid #edf2f7', verticalAlign: 'middle' },
  input: { width: '100%', padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: 3, fontSize: 13, boxSizing: 'border-box' },
  select: { width: '100%', padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: 3, fontSize: 13, boxSizing: 'border-box', background: 'white' },
  removeBtn: { background: 'none', border: 'none', color: '#e53e3e', cursor: 'pointer', fontSize: 16, padding: 4 },
  addBtn: { background: '#edf2f7', color: '#333', border: '1px solid #cbd5e0', padding: '8px 16px', borderRadius: 4, fontSize: 13, cursor: 'pointer', marginTop: 12 },
  total: { fontSize: 18, fontWeight: 700, color: '#1a365d', margin: '16px 0' },
  actions: { display: 'flex', gap: 12, marginTop: 20 },
  btn: { background: '#0078d4', color: 'white', border: 'none', padding: '10px 20px', borderRadius: 6, fontSize: 14, cursor: 'pointer', fontWeight: 600 },
  btnOutline: { background: 'white', color: '#333', border: '1px solid #cbd5e0', padding: '10px 20px', borderRadius: 6, fontSize: 14, cursor: 'pointer' },
  hint: { fontSize: 12, color: '#888', marginBottom: 12 },
  checkbox: { width: 16, height: 16, cursor: 'pointer' },
  routeSelect: { padding: '4px 6px', border: '1px solid #e2e8f0', borderRadius: 3, fontSize: 11, background: 'white' },
  saveRouteBtn: { background: 'none', border: '1px solid #cbd5e0', color: '#0078d4', cursor: 'pointer', fontSize: 10, padding: '2px 6px', borderRadius: 3 },
  routeBar: { display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12, padding: '8px 12px', background: '#f7fafc', borderRadius: 6, border: '1px solid #e2e8f0' },
  routeLabel: { fontSize: 12, fontWeight: 600, color: '#1a365d' },
  warningBox: { background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: 8, padding: '10px 14px', marginBottom: 14 },
  warningTitle: { fontSize: 13, fontWeight: 700, color: '#92400e', marginBottom: 4 },
  warningItem: { fontSize: 12, color: '#78350f', marginBottom: 2, paddingLeft: 12 },
  addMileageBtn: { background: '#b2f5ea', color: '#234e52', border: '1px solid #81e6d9', padding: '6px 14px', borderRadius: 4, fontSize: 13, cursor: 'pointer', fontWeight: 600, marginBottom: 12 },
};

export default function ReviewLineItemsStep({ expenses, receipts, reason, startDate, endDate, onChange, onNext, onBack, nextLabel, backLabel, saving }: Props) {
  const getApiToken = useApiToken();
  const receiptMap = new Map(receipts.map(r => [r.receipt_id, r]));
  const [savedRoutes, setSavedRoutes] = useState<MileageRoute[]>([]);
  const [previewReceipt, setPreviewReceipt] = useState<ReceiptMetadata | null>(null);
  const [routePreviewUrl, setRoutePreviewUrl] = useState<string | null>(null);
  const [confirmDeleteRoute, setConfirmDeleteRoute] = useState<string | null>(null);
  const [saveRouteIndex, setSaveRouteIndex] = useState<number | null>(null);
  const routesLoadedRef = useRef(false);

  useEffect(() => {
    if (routesLoadedRef.current) return;
    routesLoadedRef.current = true;
    (async () => {
      try {
        const token = await getApiToken();
        setSavedRoutes(await listMileageRoutes(token));
      } catch {
        // Non-critical
      }
    })();
  }, [getApiToken]);

  // --- Warnings: missing receipts, missing categories, missing meal days ---
  const warnings: string[] = [];

  // Check for line items without a receipt attached
  const noReceiptItems = expenses.filter(e => !e.receipt_id && e.category !== 'Mileage');
  if (noReceiptItems.length > 0) {
    warnings.push(`${noReceiptItems.length} line item${noReceiptItems.length > 1 ? 's have' : ' has'} no receipt attached (${noReceiptItems.map(e => e.vendor || 'unknown').join(', ')})`);
  }

  // Detect receipt categories not represented in line items
  const lineCategories = new Set(expenses.map(e => e.category));
  const receiptCategories: Record<string, string[]> = {};
  receipts.forEach(r => {
    let cat = 'Meals';
    if (r.is_directions) cat = 'Mileage';
    else {
      const v = (r.vendor_name || '').toLowerCase();
      if (/lyft|uber|taxi|cab|toll|parking/i.test(v)) cat = 'Transp.';
      else if (/delta|united|american|southwest|frontier|spirit|jetblue|airline/i.test(v)) cat = 'Airfare';
      else if (/hotel|inn|marriott|hilton|hyatt|hampton|residence|suites|lodge/i.test(v)) cat = 'Lodging';
      else if (/hertz|avis|enterprise|budget|national|rental/i.test(v)) cat = 'Rental Car';
    }
    if (!receiptCategories[cat]) receiptCategories[cat] = [];
    receiptCategories[cat].push(r.vendor_name || r.file_name);
  });
  Object.entries(receiptCategories).forEach(([cat, vendors]) => {
    if (!lineCategories.has(cat)) {
      warnings.push(`Receipt category "${cat}" has no matching line item (${vendors.slice(0, 3).join(', ')}${vendors.length > 3 ? '...' : ''})`);
    }
  });

  // Check for days within the report date range that have no meal expense
  if (startDate && endDate) {
    const reportDates: string[] = [];
    const start = new Date(startDate + 'T00:00:00');
    const end = new Date(endDate + 'T00:00:00');
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      reportDates.push(d.toISOString().split('T')[0]);
    }
    const mealDates = new Set<string>();
    expenses.forEach(e => { if (e.date && e.category === 'Meals') mealDates.add(e.date); });
    const daysWithoutMeals = reportDates.filter(d => !mealDates.has(d));
    if (daysWithoutMeals.length > 0 && daysWithoutMeals.length < reportDates.length) {
      const formatted = daysWithoutMeals.map(d => {
        const [y, m, day] = d.split('-');
        return `${m}/${day}`;
      });
      warnings.push(`No meal expenses on: ${formatted.join(', ')} \u2014 did you forget to add meals for ${daysWithoutMeals.length === 1 ? 'this day' : 'these days'}?`);
    }
  }

  // Check if there's no mileage at all
  const hasMileage = expenses.some(e => e.category === 'Mileage');
  if (!hasMileage && expenses.length > 0) {
    warnings.push('No mileage entries found \u2014 did you drive to this event? Add mileage from saved routes above.');
  }

  /** Calculate mileage amount: miles * (round_trip ? 2 : 1) * rate */
  const calcMileageAmount = (mileage: number | null, isRoundTrip: boolean) => {
    if (!mileage) return null;
    const effective = isRoundTrip ? mileage * 2 : mileage;
    return Math.round(effective * MILEAGE_RATE * 100) / 100;
  };

  const updateItem = (index: number, field: string, value: string | boolean) => {
    const updated = [...expenses];
    const item = { ...updated[index] };

    if (field === 'amount') item.amount = typeof value === 'string' && value ? parseFloat(value) : null;
    else if (field === 'mileage') item.mileage = typeof value === 'string' && value ? parseFloat(value) : null;
    else if (field === 'is_round_trip') item.is_round_trip = value as boolean;
    else (item as any)[field] = value;

    if (field === 'category' && value === 'Mileage') {
      // Auto-calculate amount from existing mileage
      item.amount = calcMileageAmount(item.mileage, !!item.is_round_trip);
    } else if (field === 'category' && value !== 'Mileage') {
      item.mileage = null;
      item.is_round_trip = false;
    }

    // Auto-calculate mileage amount when mileage or round-trip changes
    if (item.category === 'Mileage' && (field === 'mileage' || field === 'is_round_trip')) {
      item.amount = calcMileageAmount(item.mileage, !!item.is_round_trip);
    }

    updated[index] = item;
    onChange(updated);
  };

  const applyRoute = (index: number, route: MileageRoute) => {
    const updated = [...expenses];
    const item = { ...updated[index] };
    item.vendor = `${route.origin} → ${route.destination}`;
    item.mileage = route.default_mileage;
    item.is_round_trip = route.is_round_trip;
    item.route_id = route.route_id;
    item.route_has_receipt = !!route.receipt_blob_path;
    item.amount = calcMileageAmount(route.default_mileage, route.is_round_trip);
    updated[index] = item;
    onChange(updated);
  };

  const handleSaveRoute = (index: number) => {
    setSaveRouteIndex(index);
  };

  const executeSaveRoute = async (name: string) => {
    if (saveRouteIndex === null) return;
    const exp = expenses[saveRouteIndex];
    try {
      const token = await getApiToken();
      const route = await saveMileageRoute(token, {
        name,
        origin: exp.vendor.includes('→') ? exp.vendor.split('→')[0].trim() : exp.vendor,
        destination: exp.vendor.includes('→') ? exp.vendor.split('→')[1].trim() : '',
        default_mileage: exp.mileage || 0,
        is_round_trip: exp.is_round_trip || false,
        receipt_id: exp.receipt_id || undefined,
      });
      setSavedRoutes((prev) => [...prev, route]);
      // Link the route to the current line item so View link appears
      const updated = [...expenses];
      const item = { ...updated[saveRouteIndex] };
      item.route_id = route.route_id;
      item.route_has_receipt = !!route.receipt_blob_path;
      updated[saveRouteIndex] = item;
      onChange(updated);
    } catch (e) {
      alert(`Failed to save route: ${e}`);
    }
    setSaveRouteIndex(null);
  };

  const addItem = () => {
    onChange([...expenses, { date: '', vendor: '', reason, category: 'Meals', amount: null, mileage: null }]);
  };

  const removeItem = (index: number) => {
    onChange(expenses.filter((_, i) => i !== index));
  };

  const grandTotal = expenses.reduce((sum, e) => {
    if (e.amount) return sum + e.amount;
    // Fallback for mileage items without pre-calculated amount
    if (e.category === 'Mileage' && e.mileage) {
      const effectiveMileage = e.is_round_trip ? e.mileage * 2 : e.mileage;
      return sum + effectiveMileage * MILEAGE_RATE;
    }
    return sum;
  }, 0);

  const addMileageFromRoute = (route: MileageRoute) => {
    onChange([...expenses, {
      date: '',
      vendor: `${route.origin} \u2192 ${route.destination}`,
      reason,
      category: 'Mileage',
      amount: calcMileageAmount(route.default_mileage, route.is_round_trip),
      mileage: route.default_mileage,
      is_round_trip: route.is_round_trip,
      route_id: route.route_id,
      route_has_receipt: !!route.receipt_blob_path,
    }]);
  };

  const handleUploadRouteReceipt = async (routeId: string) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.jpg,.jpeg,.png,.pdf';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const token = await getApiToken();
        const updated = await uploadRouteReceipt(token, routeId, file);
        setSavedRoutes(prev => prev.map(r => r.route_id === routeId ? updated : r));
      } catch (e) {
        alert(`Upload failed: ${e}`);
      }
    };
    input.click();
  };

  const handleDeleteRoute = async (routeId: string) => {
    setConfirmDeleteRoute(routeId);
  };

  const executeDeleteRoute = async () => {
    if (!confirmDeleteRoute) return;
    try {
      const token = await getApiToken();
      await deleteMileageRoute(token, confirmDeleteRoute);
      setSavedRoutes(prev => prev.filter(r => r.route_id !== confirmDeleteRoute));
    } catch (e) {
      alert(`Delete failed: ${e}`);
    }
    setConfirmDeleteRoute(null);
  };

  /** Opens the route's directions receipt preview in the modal iframe. If the
   * backend 404s, the iframe shows an error page; we do NOT mutate local state,
   * since transient failures (network blips, 500s, CORS) would otherwise kill
   * the View button permanently. */
  const previewRouteReceipt = async (routeId: string) => {
    try {
      const token = await getApiToken();
      setRoutePreviewUrl(getRouteReceiptPreviewUrl(token, routeId));
    } catch {}
  };

  return (
    <div>
      <p style={styles.hint}>Mileage rate: ${MILEAGE_RATE.toFixed(2)}/mile. Max 34 line items per report.</p>

      {warnings.length > 0 && (
        <div style={styles.warningBox}>
          <div style={styles.warningTitle}>&#9888; Warnings</div>
          {warnings.map((w, i) => (
            <div key={i} style={styles.warningItem}>&bull; {w}</div>
          ))}
        </div>
      )}

      {savedRoutes.length > 0 && (() => {
        const hasMileage = expenses.some(e => e.category === 'Mileage');
        const barStyle = hasMileage ? styles.routeBar : {
          ...styles.routeBar,
          background: '#e6fffa',
          border: '2px solid #38b2ac',
          padding: '12px 16px',
        };
        return (
        <div style={{...barStyle, flexWrap: 'wrap' as const}}>
          <span style={{...styles.routeLabel, width: '100%', marginBottom: 6}}>
            {hasMileage ? 'Saved Mileage Routes:' : '\uD83D\uDE97 No mileage added \u2014 add from your saved routes:'}
          </span>
          {savedRoutes.map((route) => (
            <div key={route.route_id} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, background: 'white', border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 10px' }}>
              <button
                style={styles.addMileageBtn}
                onClick={() => addMileageFromRoute(route)}
                disabled={expenses.length >= 34}
              >
                + {route.name} ({route.default_mileage} mi{route.is_round_trip ? ', RT' : ''})
              </button>
              {route.receipt_blob_path ? (
                <span
                  style={{ fontSize: 10, color: '#0078d4', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}
                  title="Click to preview directions receipt"
                  onClick={() => previewRouteReceipt(route.route_id)}
                >&#10003; View Receipt</span>
              ) : (
                <button
                  style={{ background: 'none', border: '1px dashed #a0aec0', color: '#718096', fontSize: 10, padding: '2px 6px', borderRadius: 3, cursor: 'pointer' }}
                  onClick={() => handleUploadRouteReceipt(route.route_id)}
                  title="Attach a directions PDF/image to this route"
                >
                  + Receipt
                </button>
              )}
              <button
                style={{ background: 'none', border: 'none', color: '#e53e3e', cursor: 'pointer', fontSize: 14, padding: '0 2px' }}
                onClick={() => handleDeleteRoute(route.route_id)}
                title="Delete this saved route"
              >
                &times;
              </button>
            </div>
          ))}
        </div>
        );
      })()}

      <table style={styles.table}>
        <thead>
          <tr>
            <th style={{ ...styles.th, width: 60 }}>Receipt</th>
            <th style={{ ...styles.th, width: 110 }}>Date</th>
            <th style={styles.th}>Vendor</th>
            <th style={{ ...styles.th, width: 160 }}>Category</th>
            <th style={{ ...styles.th, width: 90 }}>Mileage</th>
            <th style={{ ...styles.th, width: 80 }}>Round Trip</th>
            <th style={{ ...styles.th, width: 100 }}>Amount</th>
            <th style={{ ...styles.th, width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {expenses.map((exp, i) => (
            <tr key={i}>
              <td style={{ ...styles.td, textAlign: 'center' }}>
                {(() => {
                  const cat = getCategoryStyle(exp.category);
                  const receipt = exp.receipt_id ? receiptMap.get(exp.receipt_id) : null;
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ fontSize: 22, lineHeight: 1 }} title={exp.category}>{cat.icon}</span>
                      {receipt && (
                        <span
                          onClick={() => setPreviewReceipt(receipt)}
                          style={{ color: '#0078d4', fontSize: 10, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}
                          title="Click to preview receipt"
                        >
                          View
                        </span>
                      )}
                      {!receipt && exp.route_id && exp.route_has_receipt && (
                        <span
                          onClick={() => previewRouteReceipt(exp.route_id!)}
                          style={{ color: '#0078d4', fontSize: 10, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}
                          title="Click to preview directions"
                        >
                          View
                        </span>
                      )}
                    </div>
                  );
                })()}
              </td>
              <td style={styles.td}>
                <input style={styles.input} type="date" value={exp.date} onChange={(e) => updateItem(i, 'date', e.target.value)} />
              </td>
              <td style={styles.td}>
                <input style={styles.input} value={exp.vendor} onChange={(e) => updateItem(i, 'vendor', e.target.value)} placeholder="Vendor name" />
              </td>
              <td style={styles.td}>
                <select style={styles.select} value={exp.category} onChange={(e) => updateItem(i, 'category', e.target.value)}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </td>
              <td style={styles.td}>
                <input
                  style={styles.input}
                  type="number"
                  step="0.1"
                  value={exp.mileage ?? ''}
                  disabled={exp.category !== 'Mileage'}
                  onChange={(e) => updateItem(i, 'mileage', e.target.value)}
                  placeholder="Miles"
                />
                {exp.category === 'Mileage' && savedRoutes.length > 0 && (
                  <select
                    style={{ ...styles.routeSelect, marginTop: 4, width: '100%' }}
                    value=""
                    onChange={(e) => {
                      const route = savedRoutes.find((r) => r.route_id === e.target.value);
                      if (route) applyRoute(i, route);
                    }}
                  >
                    <option value="">Use saved route...</option>
                    {savedRoutes.map((route) => (
                      <option key={route.route_id} value={route.route_id}>{route.name}</option>
                    ))}
                  </select>
                )}
                {exp.category === 'Mileage' && exp.mileage && (
                  <button style={{ ...styles.saveRouteBtn, marginTop: 4 }} onClick={() => handleSaveRoute(i)}>
                    Save Route
                  </button>
                )}
              </td>
              <td style={{ ...styles.td, textAlign: 'center' }}>
                {exp.category === 'Mileage' ? (
                  <input
                    type="checkbox"
                    style={styles.checkbox}
                    checked={!!exp.is_round_trip}
                    onChange={(e) => updateItem(i, 'is_round_trip', e.target.checked)}
                  />
                ) : (
                  <span style={{ color: '#ccc' }}>--</span>
                )}
              </td>
              <td style={styles.td}>
                <input
                  style={styles.input}
                  type="number"
                  step="0.01"
                  value={exp.amount ?? ''}
                  disabled={exp.category === 'Mileage'}
                  onChange={(e) => updateItem(i, 'amount', e.target.value)}
                  placeholder="$0.00"
                />
              </td>
              <td style={styles.td}>
                <button style={styles.removeBtn} onClick={() => removeItem(i)} title="Remove">&times;</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <button style={styles.addBtn} onClick={addItem} disabled={expenses.length >= 34}>+ Add Line Item</button>

      <p style={styles.total}>Grand Total: ${grandTotal.toFixed(2)}</p>

      <div style={styles.actions}>
        <button style={styles.btnOutline} onClick={onBack}>{backLabel || 'Back'}</button>
        <button style={styles.btn} onClick={onNext} disabled={expenses.length === 0 || saving}>{saving ? 'Saving...' : (nextLabel || 'Next: Generate Report')}</button>
      </div>

      {/* Receipt preview modal */}
      <ReceiptPreviewModal receipt={previewReceipt} onClose={() => setPreviewReceipt(null)} />

      {/* Route receipt preview modal */}
      {routePreviewUrl && (
        <div
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 }}
          onClick={() => setRoutePreviewUrl(null)}
        >
          <div
            style={{ background: 'white', borderRadius: 12, maxWidth: 900, maxHeight: '90vh', width: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #e2e8f0', background: '#f7fafc' }}>
              <span style={{ fontSize: 15, fontWeight: 600, color: '#1a365d' }}>Mileage Route Directions</span>
              <button style={{ background: 'none', border: 'none', fontSize: 28, color: '#666', cursor: 'pointer', padding: '0 4px', lineHeight: 1 }} onClick={() => setRoutePreviewUrl(null)}>&times;</button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f0f0', minHeight: 400 }}>
              <iframe src={routePreviewUrl} style={{ width: '100%', height: '75vh', border: 'none' }} title="Route directions" />
            </div>
          </div>
        </div>
      )}

      {/* Confirm delete route modal */}
      <ConfirmModal
        open={!!confirmDeleteRoute}
        title="Delete Saved Route"
        message="Are you sure you want to delete this saved mileage route? This cannot be undone."
        confirmLabel="Delete"
        danger
        onConfirm={executeDeleteRoute}
        onCancel={() => setConfirmDeleteRoute(null)}
      />

      {/* Save route name input modal */}
      <InputModal
        open={saveRouteIndex !== null}
        title="Save Mileage Route"
        message="Enter a name to save this route for quick reuse."
        inputPlaceholder="e.g., Drive to airport"
        confirmLabel="Save Route"
        onConfirm={executeSaveRoute}
        onCancel={() => setSaveRouteIndex(null)}
      />
    </div>
  );
}
